import { CrescLoader } from "./LoadingScreen";
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Bell, Check, CheckCheck, Building2, X } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const TYPE_ICON = {
  business_invite: <Building2 className="w-4 h-4 text-[#5A90F5]" />,
};

function NotificationRow({ notif, onRead, onRespond, onDelete }) {
  const [responding, setResponding] = useState(false);
  const isPending = notif.type === "business_invite" && !notif.responded;

  const respond = async (action) => {
    setResponding(true);
    try {
      await onRespond(notif.notification_id, action);
      toast.success(action === "accept" ? `Successfully joined ${notif?.data?.org_name || "the organization"}!` : "Invitation declined");
    } catch {
      toast.error("Could not respond. Try again.");
    }
    setResponding(false);
  };

  return (
    <div
      className={`px-4 py-3 border-b border-[#1a1a1a] hover:bg-[#0f0f0f] transition-colors group relative ${!notif.read ? "bg-[#0d1117]" : ""}`}
      onClick={() => !notif.read && onRead(notif.notification_id)}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex-shrink-0">
          {TYPE_ICON[notif.type] || <Bell className="w-4 h-4 text-[#A1A1AA]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className={`text-[12px] font-medium truncate ${notif.read ? "text-[#A1A1AA]" : "text-[#EDEDED]"}`}>
              {notif.title}
            </p>
            {!notif.read && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#5A90F5] flex-shrink-0" />
            )}
          </div>
          <p className="text-[11px] text-[#52525B] leading-relaxed">{notif.body}</p>
          {notif.data?.org_name && (
            <p className="text-[10px] text-[#3F3F46] mt-0.5">
              Organization: <span className="text-[#5A90F5]">{notif.data.org_name}</span>
            </p>
          )}
          {isPending && (
            <div className="flex gap-2 mt-2">
              <button
                disabled={responding}
                onClick={e => { e.stopPropagation(); respond("accept"); }}
                className="px-3 py-1 rounded bg-[#5A90F5] text-[#0A0A0A] text-[11px] font-semibold hover:bg-[#7AA5F8] disabled:opacity-50 transition-colors"
              >
                Accept
              </button>
              <button
                disabled={responding}
                onClick={e => { e.stopPropagation(); respond("decline"); }}
                className="px-3 py-1 rounded border border-[#2A2A2A] text-[#A1A1AA] text-[11px] hover:text-[#EDEDED] hover:bg-[#1a1a1a] disabled:opacity-50 transition-colors"
              >
                Decline
              </button>
            </div>
          )}
          {notif.responded && (
            <p className="text-[10px] text-[#3F3F46] mt-1 capitalize">
              {notif.responded === "accept" ? "✓ Accepted" : notif.responded === "decline" ? "✗ Declined" : "Responded"}
            </p>
          )}
          <p className="text-[10px] text-[#3F3F46] mt-1">
            {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function InboxPanel({ fullscreen = false }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/inbox");
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      // 404 = inbox not yet available for this account; treat as empty
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.post(`/me/inbox/${id}/read`);
    setNotifications(n => n.map(x => x.notification_id === id ? { ...x, read: true } : x));
  };

  const markAllRead = async () => {
    await api.post("/me/inbox/read-all");
    setNotifications(n => n.map(x => ({ ...x, read: true })));
  };

  const deleteNotif = async (id) => {
    try {
      await api.delete(`/me/inbox/${id}`);
      setNotifications(n => n.filter(x => x.notification_id !== id));
    } catch { toast.error("Couldn't delete notification"); }
  };

  const respond = async (id, action) => {
    await api.post(`/me/inbox/${id}/respond`, { action });
    setNotifications(n => n.map(x =>
      x.notification_id === id ? { ...x, read: true, responded: action } : x
    ));
  };

  const unread = notifications.filter(n => !n.read).length;

  return (
    <div className={fullscreen ? "flex flex-col h-full bg-[#0A0A0A]" : "flex flex-col h-full"}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-[#EDEDED] uppercase tracking-wider">Inbox</span>
          {unread > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-[#5A90F5] text-[#0A0A0A] text-[9px] font-bold">
              {unread}
            </span>
          )}
        </div>
        {unread > 0 && (
          <button onClick={markAllRead}
            className="flex items-center gap-1 text-[10px] text-[#52525B] hover:text-[#A1A1AA] transition-colors">
            <CheckCheck className="w-3 h-3" /> Mark all read
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <CrescLoader size={16} color="#5A90F5" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-8 h-8 text-[#2A2A2A] mx-auto mb-2" />
            <p className="text-[11px] text-[#3F3F46]">No notifications</p>
          </div>
        ) : (
          notifications.map(n => (
            <NotificationRow
              key={n.notification_id}
              notif={n}
              onRead={markRead}
              onRespond={respond}
            />
          ))
        )}
      </div>
    </div>
  );
}

export function InboxBadge() {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/me/inbox");
        setUnread(Array.isArray(data) ? data.filter(n => !n.read).length : 0);
      } catch {
        setUnread(0);
      }
    };
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, []);

  if (!unread) return null;
  return (
    <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#5A90F5] text-[#0A0A0A] text-[8px] font-bold flex items-center justify-center">
      {unread > 9 ? "9+" : unread}
    </span>
  );
}
